import axios from 'axios'

import { httpHelper } from '../utils'
import store from '@/store'
import jwtDecode from 'jwt-decode'

const foreignTokenUrl = '/foreign-token/'

export const userService = {
    login,
    logout,
    refreshAccessToken,
    fetchUserInfo,
    updateUserInfo,
    fetchUserToken,
    fetchUserAlertSettings,
    identifyBackendFromUsername: identifyBackendFromUsername,
    identifyBackendFromJWT: identifyBackendFromJWT,
}

async function identifyBackendFromUsername(username) {
    const backends = process.env.VUE_APP_API_URL.split(',')
    const jwt_urls = process.env.VUE_APP_JWT_API_URL.split(',')
    const websocket_urls = process.env.VUE_APP_WEBSOCKET_URL.split(',')
    const sharding_tokens = process.env.VUE_APP_SHARDING_TOKEN.split(',')

    // if only 1 backend is configured, don't look further
    if (backends.length === 1) {
        const backend_dict = {
            api_url: backends[0],
            jwt_url: jwt_urls[0],
            websocket_url: websocket_urls[0],
        }
        return [backend_dict]
    }
    // try later when the username is known
    if (!username) {
        return []
    }
    // if multiple backends are configured, return the first one containing that username
    let found_backends = []
    for (let i = 0; i < backends.length; i++) {
        try {
            const response = await axios.get(
                backends[i] + 'v1/users-for-sharding',
                {
                    headers: {
                        Authorization: `Token ${sharding_tokens[i]}`,
                    },
                }
            )

            const users = response.data.results

            const containsUser = users.some(user => user.username === username)

            if (containsUser) {
                const backend_dict = {
                    api_url: backends[i],
                    jwt_url: jwt_urls[i],
                    websocket_url: websocket_urls[i],
                }
                found_backends.push(backend_dict)
            }
        } catch (error) {
            if (error.response?.status === 401) {
                logout()
            } else {
                console.error(error)
            }
        }
    }

    return found_backends
}

async function identifyBackendFromJWT(access) {
    const backends = process.env.VUE_APP_API_URL.split(',')
    const jwt_urls = process.env.VUE_APP_JWT_API_URL.split(',')
    const websocket_urls = process.env.VUE_APP_WEBSOCKET_URL.split(',')

    // if only 1 backend is configured, don't look further
    if (backends.length === 1) {
        const backend_dict = {
            api_url: backends[0],
            jwt_url: jwt_urls[0],
            websocket_url: websocket_urls[0],
        }
        return backend_dict
    }

    const backend_site = jwtDecode(access).backend
    if (!backend_site) {
        return
    }

    for (let i = 0; i < backends.length; i++) {
        if (backends[i].startsWith(backend_site)) {
            return {
                api_url: backends[i],
                jwt_url: jwt_urls[i],
                websocket_url: websocket_urls[i],
            }
        }
    }
}

function login(username, password) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }

    return axios
        .post(
            store.state.auth.backend.jwt_url,
            JSON.stringify({ username, password }),
            requestOptions
        )
        .then(response => {
            const data = response.data
            // login successful if there's a jwt token in the response
            if (data.access) {
                // store user details and jwt token in local storage to keep user logged in between page refreshes
                localStorage.setItem('JWT', JSON.stringify(data))
            }

            return data
        })
        .catch(error => {
            if (error.response) {
                if (error.response.status === 401) {
                    logout()
                }
                return Promise.reject(error)
            }
        })
}

function logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('JWT')
    localStorage.removeItem('JWT-overwritten')
    localStorage.removeItem('backend')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('userInfo-overwritten')
}

function refreshAccessToken(refreshToken) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }

    return axios
        .post(
            store.state.auth.backend.jwt_url + 'refresh/',
            JSON.stringify({ refresh: refreshToken }),
            requestOptions
        )
        .then(response => {
            const data = response.data
            // login successful if there's a jwt token in the response
            if (data.access) {
                // store user details and jwt token in local storage to keep user logged in between page refreshes
                localStorage.setItem('JWT', JSON.stringify(data))
            }

            return data
        })
        .catch(error => {
            if (error.response) {
                if (error.response.status === 401) {
                    logout()
                }
                return Promise.reject()
            }
        })
}

async function fetchUserInfo(id) {
    const httpConfig = await httpHelper.getOverwrittenConfig()

    const { data } = await httpHelper.get(`/users/${id}/`, httpConfig)
    data.alertsettings = await fetchUserAlertSettings(id)

    return data
}

async function updateUserInfo(id, userInfo) {
    const httpConfig = await httpHelper.getOverwrittenConfig()

    const { data } = await httpHelper.patch(
        `/users/${id}/`,
        userInfo,
        httpConfig
    )

    return data
}

async function fetchUserAlertSettings(id) {
    const httpConfig = await httpHelper.getOverwrittenConfig()

    const { data } = await httpHelper.get(
        `/users/${id}/alertsettings/`,
        httpConfig
    )

    return data
}

async function fetchUserToken(id) {
    const httpConfig = await httpHelper.getOverwrittenConfig()

    const { data } = await httpHelper.post(
        foreignTokenUrl,
        { foreign_user: id },
        httpConfig,
        { apiVersion: '/' }
    )

    return data
}
